import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTimes, faCheck, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';

export default function PropertyTasks({ propertyId, clientId }) {
    // Lists
    const [clientTasks, setClientTasks] = useState([]);
    const [propertyTasks, setPropertyTasks] = useState([]);

    // Add row (Property only) - default report required true
    const [newPropertyTaskInstruction, setNewPropertyTaskInstruction] = useState('');
    const [newPropertyTaskReportRequired, setNewPropertyTaskReportRequired] = useState(true);

    // Edit state (Property only)
    const [editingKey, setEditingKey] = useState(null); // "property-34"
    const [editedTask, setEditedTask] = useState({ instruction: '', is_report_required: true });

    // Processing/feedback
    const [isProcessing, setIsProcessing] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');

    const canLoadClientTasks = useMemo(() => !!clientId, [clientId]);

    const fetchClientTasks = useCallback(async () => {
        if (!canLoadClientTasks) {
            setClientTasks([]);
            return;
        }
        try {
            const res = await axios.get(`/api/tasks/client/${clientId}`);
            setClientTasks(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Error fetching client tasks:', e);
            setClientTasks([]);
        }
    }, [clientId, canLoadClientTasks]);

    const fetchPropertyTasks = useCallback(async () => {
        try {
            const res = await axios.get(`/api/tasks/property/${propertyId}`);
            setPropertyTasks(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Error fetching property tasks:', e);
            setPropertyTasks([]);
        }
    }, [propertyId]);

    useEffect(() => {
        fetchClientTasks();
    }, [fetchClientTasks]);

    useEffect(() => {
        fetchPropertyTasks();
    }, [fetchPropertyTasks]);

    // Property edit only
    const startEditPropertyTask = (task) => {
        const key = `property-${task.id}`;
        if (editingKey === key) {
            setEditingKey(null);
            setEditedTask({ instruction: '', is_report_required: true });
            return;
        }
        setEditingKey(key);
        setEditedTask({
            instruction: task.instruction || '',
            is_report_required: !!task.is_report_required,
        });
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditedTask({ instruction: '', is_report_required: true });
    };

    const saveEditPropertyTask = async (taskId) => {
        const instruction = (editedTask.instruction || '').trim();
        if (!instruction) {
            setFeedbackMessage('Please enter a task instruction.');
            return;
        }

        setIsProcessing(true);
        setFeedbackMessage('');

        try {
            await axios.put(`/api/tasks/property/${propertyId}/${taskId}`, {
                instruction,
                is_report_required: !!editedTask.is_report_required,
            });

            await fetchPropertyTasks();

            setEditingKey(null);
            setEditedTask({ instruction: '', is_report_required: true });
            setFeedbackMessage('Property task updated successfully.');
        } catch (e) {
            console.error('Error updating property task:', e);
            setFeedbackMessage(e.response?.data?.error || 'Failed to update property task.');
        } finally {
            setIsProcessing(false);
        }
    };

    const deletePropertyTask = async (taskId) => {
        const ok = window.confirm('Are you sure you want to delete this task?');
        if (!ok) return;

        setIsProcessing(true);
        setFeedbackMessage('');

        try {
            await axios.delete(`/api/tasks/property/${propertyId}/${taskId}`);
            await fetchPropertyTasks();
            setFeedbackMessage('Property task deleted successfully.');
        } catch (e) {
            console.error('Error deleting property task:', e);
            setFeedbackMessage(e.response?.data?.error || 'Failed to delete property task.');
        } finally {
            setIsProcessing(false);
        }
    };

    const addPropertyTask = async () => {
        const instruction = (newPropertyTaskInstruction || '').trim();
        if (!instruction) {
            setFeedbackMessage('Please enter a task instruction.');
            return;
        }

        setIsProcessing(true);
        setFeedbackMessage('');

        try {
            await axios.post(`/api/tasks/property/${propertyId}`, {
                instruction,
                is_report_required: !!newPropertyTaskReportRequired,
            });

            setNewPropertyTaskInstruction('');
            setNewPropertyTaskReportRequired(true);

            await fetchPropertyTasks();
            setFeedbackMessage('Property task added successfully.');
        } catch (e) {
            console.error('Error adding property task:', e);
            setFeedbackMessage(e.response?.data?.error || 'Failed to add property task.');
        } finally {
            setIsProcessing(false);
        }
    };

    const renderClientTaskRow = (task, index) => {
        return (
            <div className="client-task-card" key={`client-${task.id}`}>
                <div className="client-task-row">
                    <div className="client-task-left">
                        <span className="client-task-number">{index + 1}.</span>
                        <div className="client-task-instruction">{task.instruction}</div>
                    </div>

                    <div className="client-task-right">
                        {task.is_report_required ? (
                            <span className="client-task-badge client-task-badge-required">Report required</span>
                        ) : (
                            <span className="client-task-badge client-task-badge-optional">No report</span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderPropertyTaskRow = (task, index) => {
        const key = `property-${task.id}`;
        const isEditing = editingKey === key;

        return (
            <div className="client-task-card" key={`property-${task.id}`}>
                <div className="client-task-row">
                    <div className="client-task-left">
                        <span className="client-task-number">{index + 1}.</span>

                        {isEditing ? (
                            <input
                                className="client-tasks-input"
                                type="text"
                                value={editedTask.instruction}
                                onChange={(e) => setEditedTask((p) => ({ ...p, instruction: e.target.value }))}
                                maxLength={180}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        saveEditPropertyTask(task.id);
                                    } else if (e.key === 'Escape') {
                                        e.preventDefault();
                                        cancelEdit();
                                    }
                                }}
                                style={{ height: 36 }}
                            />
                        ) : (
                            <div className="client-task-instruction">{task.instruction}</div>
                        )}
                    </div>

                    <div className="client-task-right">
                        {isEditing ? (
                            <>
                                <label className="client-tasks-checkbox" style={{ padding: '6px 10px' }}>
                                    <input
                                        type="checkbox"
                                        checked={!!editedTask.is_report_required}
                                        onChange={(e) =>
                                            setEditedTask((p) => ({ ...p, is_report_required: e.target.checked }))
                                        }
                                    />
                                    Report required
                                </label>

                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className="user-edit-icon user-save-icon"
                                    title="Save"
                                    onClick={() => saveEditPropertyTask(task.id)}
                                />
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="user-edit-icon user-cancel-icon"
                                    title="Cancel"
                                    onClick={cancelEdit}
                                />
                            </>
                        ) : (
                            <>
                                {task.is_report_required ? (
                                    <span className="client-task-badge client-task-badge-required">Report required</span>
                                ) : (
                                    <span className="client-task-badge client-task-badge-optional">No report</span>
                                )}

                                <FontAwesomeIcon
                                    icon={faPen}
                                    className="user-edit-icon edit-user-icon"
                                    title="Edit"
                                    onClick={() => startEditPropertyTask(task)}
                                />

                                <button
                                    className="client-task-delete-btn"
                                    onClick={() => deletePropertyTask(task.id)}
                                    title="Delete task"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="dp-card client-tasks-section">
            {/* Header */}
            <div className="client-tasks-title-wrap dp-cardHead">
                <div className="client-tasks-title-block">
                    <h3 className="client-tasks-title">Client Tasks</h3>
                </div>

                <span className="client-tasks-count">{(clientTasks?.length || 0) + (propertyTasks?.length || 0)}</span>
            </div>

            {/* Client Tasks (read only) */}
            <div style={{ marginTop: 6 }}>
                <div className="client-tasks-subtitle" style={{ marginBottom: 8 }}>
                    Client Tasks can be edited through the{' '}
                    {clientId ? (
                        <Link to={`/clients/${clientId}`} style={{ color: '#FF5A60', fontWeight: 700, textDecoration: 'none' }}>
                            client page
                        </Link>
                    ) : (
                        <span style={{ color: '#6b7280', fontWeight: 600 }}>Client page</span>
                    )}
                    .
                </div>

                <div style={{ height: 1, background: '#eef0f3', margin: '14px 0' }} />

                {clientTasks.length > 0 ? (
                    <div className="client-tasks-list">
                        {clientTasks.map((task, index) => renderClientTaskRow(task, index))}
                    </div>
                ) : (
                    <div className="client-tasks-empty">
                        {canLoadClientTasks ? 'No client tasks found.' : 'Client tasks unavailable (missing client id).'}
                    </div>
                )}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#eef0f3', margin: '14px 0' }} />

            {/* Property Tasks (editable) */}
            <div>
                <div className="client-tasks-title-block" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                        <h3 className="client-tasks-title" style={{ fontSize: 16, margin: 0 }}>
                            Property Tasks
                        </h3>
                        <span className="client-tasks-count" style={{ height: 22, minWidth: 28, fontSize: 12 }}>
                            {propertyTasks.length}
                        </span>
                    </div>
                    <div className="client-tasks-subtitle">
                        These are additional tasks for this property only.
                    </div>
                </div>

                <div className="client-tasks-add">
                    <div className="client-tasks-add-row">

                        <input
                            className="client-tasks-input"
                            type="text"
                            placeholder="Add property task instruction (e.g., leave 2 keys on the counter)"
                            value={newPropertyTaskInstruction}
                            onChange={(e) => setNewPropertyTaskInstruction(e.target.value)}
                            maxLength={180}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addPropertyTask();
                                }
                            }}
                            disabled={isProcessing}
                        />

                        <label className="client-tasks-checkbox">
                            <input
                                type="checkbox"
                                checked={newPropertyTaskReportRequired}
                                onChange={(e) => setNewPropertyTaskReportRequired(e.target.checked)}
                                disabled={isProcessing}
                            />
                            Report required
                        </label>

                        <button
                            className="client-tasks-add-btn"
                            onClick={addPropertyTask}
                            disabled={isProcessing}
                            title="Add Property Task"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>Add</span>
                        </button>
                    </div>
                </div>

                {propertyTasks.length > 0 ? (
                    <div className="client-tasks-list" style={{ marginTop: 12 }}>
                        {propertyTasks.map((task, index) => renderPropertyTaskRow(task, index))}
                    </div>
                ) : (
                    <div className="client-tasks-empty" style={{ marginTop: 12 }}>
                        No property tasks yet. Add your first instruction above.
                    </div>
                )}

                {!!feedbackMessage && (
                    <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                        {feedbackMessage}
                    </div>
                )}
            </div>
        </div>
    );
}
